import React from "react";
import ProgressCircle from "components/generic/ProgressCircle";
import { Checkbox } from "components/ui/checkbox";
import FeatureList from "components/generic/FeatureList";
import { Separator } from "@radix-ui/react-select";
import { BadgeDollarSign, CircleUserRound, AlertCircle } from "lucide-react"; // Added AlertCircle

const EquipmentCard = ({ equipment, isSelected, onToggleSelect }) => {
  // Extract availability
  const isUnavailable = equipment?.is_available === 0;

  // Extract equipment data with fallbacks
  const equipmentName = equipment?.equipment_name || equipment?.name || 'Equipment';
  const equipmentModel = equipment?.model || equipment?.equipment_type || '';
  const matchScore = equipment?.match_score || 95;
  const price = equipment?.daily_rental_rate || equipment?.price || 200;
  const category = equipment?.category || equipment?.equipment_category || 'Equipment';
  const accessories = equipment?.accessories || [];
  const equipmentId = equipment?.equipment_id || equipment?.id;

  return (
    <div className={`border bg-card rounded-2xl py-6 w-full transition-all ${isUnavailable ? 'opacity-70 bg-gray-50/50' : ''}`}>
      <div className="flex px-6 pb-6 gap-4">
        <div className="w-min">
          <Checkbox
            id={`equipment-${equipmentId}`}
            checked={isSelected}
            onCheckedChange={onToggleSelect}
            disabled={isUnavailable} // Disable interaction if unavailable
            className="data-[state=checked]:border-button-coloured data-[state=checked]:bg-button-coloured data-[state=checked]:text-button-coloured-foreground "
          />
        </div>
        <div className="flex flex-col gap-1 w-full">
          <div className="flex items-start justify-between gap-2">
            <div className={`w-12 h-12 rounded-lg flex items-center justify-center font-semibold ${isUnavailable ? 'bg-gray-200 text-gray-500' : 'bg-popover text-popover-foreground'}`}>
              {equipmentName.substring(0, 2).toUpperCase()}
            </div>

            {!isUnavailable ? (
              <div className="flex items-center justify-between gap-2 py-1 px-2 rounded-full border border-blue-100 h-8">
                <ProgressCircle value={matchScore} />
                <p className="text-blue-500 text-xs font-bold px-0">{matchScore}%</p>
              </div>
            ) : (
              <div className="flex items-center gap-1 py-1 px-2 rounded-full border border-red-200 bg-red-50 h-8">
                <AlertCircle className="w-3 h-3 text-red-500" />
                <p className="text-red-600 text-[10px] font-bold uppercase">Unavailable</p>
              </div>
            )}
          </div>

          <div className="flex flex-col gap-1">
            <p className={`text-base font-semibold ${isUnavailable ? 'text-gray-500' : ''}`}>
                {equipmentName}
            </p>
            {equipmentModel && <p className="text-grey-2 text-sm">{equipmentModel}</p>}

            {/* Warning Message for Unavailable items */}
            {isUnavailable && (
              <p className="text-red-500 text-[11px] font-medium mt-1 leading-tight">
                All units are currently in use on your selected dates.
              </p>
            )}
          </div>
        </div>
      </div>
      <hr className={isUnavailable ? 'border-gray-100' : ''} />
      <div className="flex justify-center gap-4 w-full">
        <FeatureList
          list={accessories.length > 0 ? accessories : ['No accessories listed']}
          visibleCount={2}
        />
      </div>
      <hr className={isUnavailable ? 'border-gray-100' : ''} />
      <div className="flex items-center justify-center px-4 pt-4">
        <div className="flex gap-2 px-3 py-2 items-center">
          <BadgeDollarSign className={`w-4 h-4 ${isUnavailable ? 'text-gray-400' : ''}`} />
          <p className={`text-xs font-medium ${isUnavailable ? 'text-gray-400' : ''}`}>${price}/day</p>
        </div>
        <Separator orientation="vertical" className="h-5 border" />
        <div className="flex gap-2 px-3 py-2 items-center">
          <CircleUserRound className={`w-4 h-4 ${isUnavailable ? 'text-gray-400' : ''}`} />
          <p className={`text-xs font-medium ${isUnavailable ? 'text-gray-400' : ''}`}>{category}</p>
        </div>
      </div>
    </div>
  );
};

export default EquipmentCard;
